import { useState, useEffect, useCallback, useRef } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { supportService, AgentPresence } from '../services/support';
import { CallState, TwilioCallContext } from '../types/support';
import { showToast } from '../utils/helpers';

// ============================================================================
// Twilio Voice Hook for WebRTC Calling
// Multi-agent support with unique identities and presence tracking
// ============================================================================

// Heartbeat interval (30 seconds)
const HEARTBEAT_INTERVAL = 30 * 1000;

interface UseTwilioVoiceReturn {
  // State
  isReady: boolean;
  isConfigured: boolean;
  callContext: TwilioCallContext;
  incomingCall: Call | null;
  agentIdentity: string | null;
  onlineAgents: AgentPresence[];
  
  // Actions
  makeCall: (to: string, ticketId?: string) => Promise<void>;
  acceptCall: () => void;
  rejectCall: () => void;
  hangup: () => void;
  toggleMute: () => void;
  
  // Refresh token
  refreshToken: () => Promise<void>;
  
  // Presence
  refreshOnlineAgents: () => Promise<void>;
}

export function useTwilioVoice(): UseTwilioVoiceReturn {
  // Device instance
  const deviceRef = useRef<Device | null>(null);
  const activeCallRef = useRef<Call | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // State
  const [isReady, setIsReady] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);
  const [agentIdentity, setAgentIdentity] = useState<string | null>(null);
  const [onlineAgents, setOnlineAgents] = useState<AgentPresence[]>([]);
  const [callContext, setCallContext] = useState<TwilioCallContext>({
    state: 'idle',
    isMuted: false,
    duration: 0,
  });

  // Start duration timer when in call
  const startDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    const startTime = Date.now();
    durationIntervalRef.current = setInterval(() => {
      setCallContext((prev) => ({
        ...prev,
        duration: Math.floor((Date.now() - startTime) / 1000),
      }));
    }, 1000);
  }, []);

  // Stop duration timer
  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  // Reset call state
  const resetCallState = useCallback(() => {
    stopDurationTimer();
    activeCallRef.current = null;
    setCallContext({
      state: 'idle',
      isMuted: false,
      duration: 0,
    });
  }, [stopDurationTimer]);

  // Refresh online agents list
  const refreshOnlineAgents = useCallback(async () => {
    try {
      const response = await supportService.getOnlineAgents();
      setOnlineAgents(response.agents);
    } catch (error) {
      console.error('Failed to refresh online agents:', error);
    }
  }, []);

  // Start heartbeat
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    heartbeatIntervalRef.current = setInterval(async () => {
      try {
        await supportService.sendAgentHeartbeat();
        // Also refresh online agents list periodically
        await refreshOnlineAgents();
      } catch (error) {
        console.error('Heartbeat failed:', error);
      }
    }, HEARTBEAT_INTERVAL);
  }, [refreshOnlineAgents]);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Initialize Twilio Device
  const initializeDevice = useCallback(async () => {
    try {
      // Check if voice is configured
      const config = await supportService.getVoiceConfig();
      setIsConfigured(config.configured);
      
      if (!config.configured) {
        console.log('Voice calling not configured on backend');
        return;
      }

      // Get UNIQUE voice token for this agent (multi-agent support)
      // Each agent gets identity like "agent-123"
      const tokenResponse = await supportService.getVoiceToken();
      setAgentIdentity(tokenResponse.identity);
      
      console.log(`Initializing Twilio Device with identity: ${tokenResponse.identity}`);
      
      // Create new Device with recommended settings
      const device = new Device(tokenResponse.token, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        edge: 'ashburn', // Use nearest Twilio edge location
      });

      // Device events
      device.on('registered', async () => {
        console.log(`Twilio Device registered as ${tokenResponse.identity}`);
        setIsReady(true);
        
        // Register agent as online in the presence system
        try {
          await supportService.registerAgentOnline();
          console.log('Agent registered as online');
          
          // Start heartbeat to maintain online status
          startHeartbeat();
          
          // Refresh online agents list
          await refreshOnlineAgents();
          
          showToast('success', 'Voice calling ready');
        } catch (error) {
          console.error('Failed to register agent online:', error);
        }
      });

      device.on('unregistered', async () => {
        console.log('Twilio Device unregistered');
        setIsReady(false);
        stopHeartbeat();
        
        // Unregister from presence system
        try {
          await supportService.unregisterAgent();
          console.log('Agent unregistered from presence');
        } catch (error) {
          console.error('Failed to unregister agent:', error);
        }
      });

      device.on('error', (error) => {
        console.error('Twilio Device error:', error);
        showToast('error', `Voice error: ${error.message}`);
        resetCallState();
      });

      device.on('incoming', (call: Call) => {
        // Get caller info from custom parameters (passed from TwiML)
        // or fall back to standard parameters
        const customParams = call.customParameters || new Map();
        const callerPhone = customParams.get('callerPhone') || call.parameters.From;
        const callSid = customParams.get('callSid') || call.parameters.CallSid;
        
        console.log('Incoming call:', {
          from: callerPhone,
          callSid,
          customParams: Object.fromEntries(customParams),
          standardParams: call.parameters,
        });
        
        setIncomingCall(call);
        setCallContext((prev) => ({
          ...prev,
          state: 'incoming',
          callerId: callerPhone,
          callSid,
        }));

        // Set up call events for incoming
        setupCallEvents(call);
      });

      // Register the device
      await device.register();
      deviceRef.current = device;

    } catch (error) {
      console.error('Failed to initialize Twilio Device:', error);
      setIsConfigured(false);
    }
  }, [resetCallState]);

  // Set up call events
  const setupCallEvents = useCallback((call: Call) => {
    call.on('accept', async () => {
      console.log('Call accepted');
      activeCallRef.current = call;
      setCallContext((prev) => ({
        ...prev,
        state: 'in-call',
      }));
      startDurationTimer();
      
      // Set agent as busy
      try {
        await supportService.setAgentBusy(call.parameters?.CallSid);
      } catch (error) {
        console.error('Failed to set agent busy:', error);
      }
    });

    call.on('disconnect', async () => {
      console.log('Call disconnected');
      showToast('info', 'Call ended');
      resetCallState();
      setIncomingCall(null);
      
      // Set agent as available
      try {
        await supportService.setAgentAvailable();
      } catch (error) {
        console.error('Failed to set agent available:', error);
      }
    });

    call.on('cancel', async () => {
      console.log('Call canceled');
      resetCallState();
      setIncomingCall(null);
      
      // Set agent as available
      try {
        await supportService.setAgentAvailable();
      } catch (error) {
        console.error('Failed to set agent available:', error);
      }
    });

    call.on('reject', async () => {
      console.log('Call rejected');
      resetCallState();
      setIncomingCall(null);
      
      // Set agent as available
      try {
        await supportService.setAgentAvailable();
      } catch (error) {
        console.error('Failed to set agent available:', error);
      }
    });

    call.on('error', async (error) => {
      console.error('Call error:', error);
      showToast('error', 'Call failed');
      resetCallState();
      setIncomingCall(null);
      
      // Set agent as available
      try {
        await supportService.setAgentAvailable();
      } catch (error) {
        console.error('Failed to set agent available:', error);
      }
    });

    call.on('ringing', () => {
      console.log('Call ringing');
      setCallContext((prev) => ({
        ...prev,
        state: 'ringing',
      }));
    });
  }, [resetCallState, startDurationTimer]);

  // Make outbound call
  const makeCall = useCallback(async (to: string, ticketId?: string) => {
    if (!deviceRef.current || !isReady) {
      showToast('error', 'Voice is not ready. Please wait...');
      return;
    }

    if (callContext.state !== 'idle') {
      showToast('error', 'A call is already in progress');
      return;
    }

    try {
      setCallContext({
        state: 'connecting',
        isMuted: false,
        duration: 0,
        callerId: to,
        ticketId,
      });

      // Connect the call with params for the TwiML app
      const call = await deviceRef.current.connect({
        params: {
          To: to,
          TicketId: ticketId || '',
        },
      });

      activeCallRef.current = call;
      setupCallEvents(call);

    } catch (error) {
      console.error('Failed to make call:', error);
      showToast('error', 'Failed to connect call');
      resetCallState();
    }
  }, [isReady, callContext.state, setupCallEvents, resetCallState]);

  // Accept incoming call
  const acceptCall = useCallback(() => {
    if (incomingCall) {
      incomingCall.accept();
      setIncomingCall(null);
    }
  }, [incomingCall]);

  // Reject incoming call
  const rejectCall = useCallback(() => {
    if (incomingCall) {
      incomingCall.reject();
      setIncomingCall(null);
      resetCallState();
    }
  }, [incomingCall, resetCallState]);

  // Hangup active call
  const hangup = useCallback(() => {
    if (activeCallRef.current) {
      activeCallRef.current.disconnect();
    }
    if (incomingCall) {
      incomingCall.reject();
      setIncomingCall(null);
    }
    resetCallState();
  }, [incomingCall, resetCallState]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (activeCallRef.current) {
      const newMuteState = !callContext.isMuted;
      activeCallRef.current.mute(newMuteState);
      setCallContext((prev) => ({
        ...prev,
        isMuted: newMuteState,
      }));
    }
  }, [callContext.isMuted]);

  // Refresh token
  const refreshToken = useCallback(async () => {
    try {
      // Use unique token for this agent
      const tokenResponse = await supportService.getVoiceToken();
      if (deviceRef.current) {
        await deviceRef.current.updateToken(tokenResponse.token);
        console.log('Token refreshed successfully');
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      showToast('error', 'Failed to refresh voice token');
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    initializeDevice();

    // Cleanup function
    return () => {
      // Unregister agent on unmount
      supportService.unregisterAgent().catch(console.error);
      
      stopHeartbeat();
      stopDurationTimer();
      
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
    };
  }, [initializeDevice, stopDurationTimer, stopHeartbeat]);

  // Token refresh interval (every 50 minutes, token expires in 60)
  useEffect(() => {
    if (!isReady) return;

    const refreshInterval = setInterval(() => {
      refreshToken();
    }, 50 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [isReady, refreshToken]);

  return {
    isReady,
    isConfigured,
    callContext,
    incomingCall,
    agentIdentity,
    onlineAgents,
    makeCall,
    acceptCall,
    rejectCall,
    hangup,
    toggleMute,
    refreshToken,
    refreshOnlineAgents,
  };
}

export default useTwilioVoice;
